import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'
import { SignInButton } from '@/components/sign-in-button'
import {
  IconTrendingUp,
  IconSparkles,
  IconClock,
  IconClockHour4,
  IconFileText,
  IconMailOff,
  IconBellOff,
  IconTrash,
  IconCircleCheck,
  IconArrowForward,
  IconCalendarEvent,
  IconCheck,
  IconKeyboard,
  IconFilter,
  IconAlarm
} from '@tabler/icons-react'

export const Route = createFileRoute('/email-productivity')({
  component: EmailProductivityPage,
})

function EmailProductivityPage() {
  const problemStats = [
    { value: "28%", label: "of workday on email", detail: "Over 2 hours daily" },
    { value: "11 min", label: "to refocus", detail: "After each interruption" },
    { value: "36x", label: "email checks", detail: "Average per hour" },
  ]

  const strategies = [
    {
      icon: <IconClockHour4 className="w-6 h-6" />,
      title: "Batch Process Your Email",
      description: "Process email in 2-3 dedicated sessions per day instead of checking constantly.",
      schedule: [
        { time: "Morning", task: "Quick scan for urgent items" },
        { time: "Midday", task: "Main processing session" },
        { time: "End of day", task: "Final sweep and tomorrow's plan" }
      ]
    },
    {
      icon: <IconClock className="w-6 h-6" />,
      title: "Use the Two-Minute Rule",
      description: "If an email takes less than two minutes to handle, do it immediately. This prevents small tasks from accumulating."
    },
    {
      icon: <IconFileText className="w-6 h-6" />,
      title: "Write Shorter Emails",
      description: "Shorter emails get faster responses. Aim for 5 sentences or less. If it requires more, consider whether a call would be more efficient."
    },
    {
      icon: <IconMailOff className="w-6 h-6" />,
      title: "Unsubscribe Aggressively",
      description: "Every newsletter you don't read is a future interruption. Unsubscribe ruthlessly from anything that doesn't provide clear value."
    },
    {
      icon: <IconBellOff className="w-6 h-6" />,
      title: "Turn Off Notifications",
      description: "Email notifications are productivity killers. Turn them off and check email on your schedule, not your inbox's."
    }
  ]

  const fourDs = [
    { letter: "D", action: "Delete", description: "Remove emails you don't need", icon: <IconTrash className="w-5 h-5" />, color: "red" },
    { letter: "D", action: "Do", description: "Handle quick tasks immediately", icon: <IconCircleCheck className="w-5 h-5" />, color: "green" },
    { letter: "D", action: "Delegate", description: "Forward to the right person", icon: <IconArrowForward className="w-5 h-5" />, color: "orange" },
    { letter: "D", action: "Defer", description: "Schedule time for complex responses", icon: <IconCalendarEvent className="w-5 h-5" />, color: "blue" },
  ]

  const tools = [
    { icon: <IconKeyboard className="w-5 h-5" />, title: "Keyboard shortcuts", description: "Learn Gmail shortcuts to process emails faster" },
    { icon: <IconFilter className="w-5 h-5" />, title: "Filters", description: "Auto-sort low-priority emails out of your inbox" },
    { icon: <IconAlarm className="w-5 h-5" />, title: "Snooze", description: "Defer emails to when you can actually handle them" },
    { icon: <IconSparkles className="w-5 h-5" />, title: "AI tools", description: "Use Winbox Zero to auto-categorize emails" },
  ]

  const metrics = [
    "How many times per day do you check email?",
    "How long does each email session take?",
    "How many emails do you receive vs. actually need to read?",
    "How often do you achieve inbox zero?"
  ]

  return (
    <div className="bg-[#fafafa] text-slate-950 min-h-screen">
      <StaticNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-orange-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-orange-50 border border-orange-100">
              <IconTrendingUp className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-700">
                Work smarter, not harder
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Email Productivity:{' '}
              <span className="text-orange-600">Spend Less Time on Email</span>
            </h1>

            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Practical strategies to reduce time spent on email while staying on top
              of important communications.
            </p>

            {/* Problem Stats */}
            <div className="grid md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              {problemStats.map((stat, i) => (
                <div key={i} className="bg-white rounded-3xl border border-slate-200 p-5 text-center shadow-sm">
                  <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                  <div className="text-sm font-medium text-slate-700">{stat.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{stat.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategies */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Email Productivity Strategies
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Proven techniques to take control of your inbox
          </p>

          <div className="space-y-6">
            {strategies.map((strategy, i) => (
              <div key={i} className="flex gap-6 p-6 rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-3xl bg-orange-100 text-orange-600 flex items-center justify-center">
                    {strategy.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{strategy.title}</h3>
                  <p className="text-slate-600 mb-4">{strategy.description}</p>
                  {strategy.schedule && (
                    <div className="flex flex-wrap gap-3">
                      {strategy.schedule.map((item, j) => (
                        <div key={j} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200">
                          <span className="text-xs font-medium text-orange-600">{item.time}</span>
                          <span className="text-sm text-slate-600">{item.task}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 4 D's */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            The 4 D's of Email Productivity
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            A simple framework for processing every email
          </p>

          <div className="grid md:grid-cols-4 gap-4">
            {fourDs.map((d, i) => {
              const colorClasses = {
                red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-100 text-red-600', letter: 'text-red-600' },
                green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600', letter: 'text-green-600' },
                orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600', letter: 'text-orange-600' },
                blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600', letter: 'text-blue-600' },
              }[d.color] || { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'bg-slate-100 text-slate-600', letter: 'text-slate-600' }

              return (
                <div key={i} className={`${colorClasses.bg} ${colorClasses.border} border-2 rounded-3xl p-6 text-center`}>
                  <div className={`w-12 h-12 rounded-lg ${colorClasses.icon} flex items-center justify-center mx-auto mb-4`}>
                    {d.icon}
                  </div>
                  <h3 className={`text-xl font-bold ${colorClasses.letter} mb-2`}>{d.action}</h3>
                  <p className="text-sm text-slate-600">{d.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Email Productivity Tools
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Tools to boost your email efficiency
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tools.map((tool, i) => (
              <div key={i} className="p-6 rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 hover:border-orange-200 hover:bg-orange-50/50 transition-colors">
                <div className="w-12 h-12 rounded-3xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
                  {tool.icon}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{tool.title}</h3>
                <p className="text-sm text-slate-600">{tool.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Measuring */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Measuring Email Productivity
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Track your email habits to identify areas for improvement
          </p>

          <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl p-8">
            <div className="grid md:grid-cols-2 gap-4">
              {metrics.map((metric, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-3xl p-4 shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                  <p className="text-slate-700 pt-1">{metric}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-20 shadow-2xl sm:px-16 sm:py-24 text-center">
            <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl mix-blend-screen" />
            <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl mix-blend-screen" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Boost your email productivity
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-300">
                Get started today with unlimited email processing.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  )
}
